import { Button, Card, Col, Input, Row, Select, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./_styles.less";
import { useSelector } from "react-redux";
import { acessosSelector } from "@/store/acessos";
import moment from "moment";
import { useSearchParams } from "@/hooks/useSearchParams";
import { empresasSelector } from "@/store/empresas";
import { ClearOutlined } from "@ant-design/icons";
import useModulo from "@/hooks/useModulo";
import MaskedRangePicker from "@/components/masked-range-picker";

const PERIODOS = {
  mesAtual: [moment().startOf("month"), moment()],
  mesAnterior: [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
  anoAtual: [moment().startOf("year"), moment()],
  anoAnterior: [moment().subtract(1, "year").startOf("year"), moment().subtract(1, "year").endOf("year")],
};

export default function DashboardFiltros({ onFiltersChange }) {
  const { t } = useTranslation();
  const acessos = useSelector(acessosSelector);
  const { acessoModulosObjects } = useModulo();

  const empresas = useSelector(empresasSelector);
  const [searchParams, setSearchParams] = useSearchParams();

  const [modulosSelecionados, setModulosSelecionados] = useState([]);
  const [acessoSelecionado, setAcessoSelecionado] = useState(null);
  const [empresasSelecionadas, setEmpresasSelecionadas] = useState([]);
  const [empresasByAcesso, setEmpresasByAcesso] = useState([]);
  const [periodoSelecionado, setPeriodoSelecionado] = useState({ opcao: "mesAtual", arrayDatas: PERIODOS.mesAtual });

  const handlePeriodoChange = (value) => {
    const arraysDatas = PERIODOS[value];
    setPeriodoSelecionado({ opcao: value, arrayDatas: arraysDatas });
  };

  const handleLimparFiltros = () => {
    setModulosSelecionados([]);
    setAcessoSelecionado(null);
    setEmpresasSelecionadas([]);
    setPeriodoSelecionado({
      opcao: "mesAtual",
      arrayDatas: PERIODOS.mesAtual,
    });
  };

  useEffect(() => {
    const start = searchParams.get("startDate");
    const end = searchParams.get("endDate");
    const modulos = searchParams.get("modulosNames")?.split(",").filter(Boolean) || [];
    const empresas = searchParams.get("empresasIds")?.split(",").filter(Boolean) || [];
    const acesso = searchParams.get("acessoId") || null;
    const opcao = searchParams.get("opcao");

    if (start && end) {
      setPeriodoSelecionado({
        opcao: opcao,
        arrayDatas: [moment(start), moment(end)],
      });
    }

    if (modulos.length) setModulosSelecionados(modulos);
    if (empresas.length) setEmpresasSelecionadas(empresas);
    if (acesso) setAcessoSelecionado(acesso);
  }, []);

  useEffect(() => {
    if (acessoSelecionado && empresas.length > 0) {
      setEmpresasByAcesso(empresas.filter((e) => e.acesso.id === Number(acessoSelecionado)));
    }
  }, [acessoSelecionado, empresas]);

  useEffect(() => {
    const params = {};
    if (periodoSelecionado.arrayDatas?.[0]) params.startDate = periodoSelecionado.arrayDatas[0].format("YYYY-MM-DD");
    if (periodoSelecionado.arrayDatas?.[1]) params.endDate = periodoSelecionado.arrayDatas[1].format("YYYY-MM-DD");
    if (periodoSelecionado.opcao) params.opcao = periodoSelecionado.opcao;
    if (acessoSelecionado) params.acessoId = acessoSelecionado;
    if (empresasSelecionadas.length > 0) params.empresasIds = empresasSelecionadas.toString();
    if (modulosSelecionados.length > 0) params.modulosNames = modulosSelecionados.toString();

    setSearchParams(params);
    onFiltersChange?.(params);
  }, [periodoSelecionado, modulosSelecionados, acessoSelecionado, empresasSelecionadas]);

  return (
    <Row gutter={[16, 16]} className={styles["row-dashboard"]}>
      <Col span={24}>
        <Card className={styles["card-filtros"]}>
          <Row gutter={[8, 8]} align="middle">
            <Col flex="1 1 250px">
              <Tooltip title={t("Filtrar por período")}>
                <Input.Group compact className={styles["date-selector"]}>
                  <Select
                    defaultValue="mesAtual"
                    options={[
                      { label: t("Mês atual"), value: "mesAtual" },
                      { label: t("Mês anterior"), value: "mesAnterior" },
                      { label: t("Ano atual"), value: "anoAtual" },
                      { label: t("Ano anterior"), value: "anoAnterior" },
                      { label: t("Personalizado"), value: "personalizado" },
                    ]}
                    value={periodoSelecionado.opcao}
                    onChange={handlePeriodoChange}
                  />
                  <MaskedRangePicker
                    disabled={!(periodoSelecionado.opcao === "personalizado")}
                    value={periodoSelecionado.arrayDatas}
                    onChange={(dates) => setPeriodoSelecionado({ ...periodoSelecionado, arrayDatas: dates })}
                    className={styles["range-picker"]}
                  />
                </Input.Group>
              </Tooltip>
            </Col>
            <Col flex="1 1 150px">
              <Tooltip title={t("Filtrar por módulos")}>
                <Select
                  showArrow
                  mode="multiple"
                  allowClear
                  placeholder={t("Módulos")}
                  style={{ width: "100%" }}
                  options={acessoModulosObjects
                    .filter((item) => item.id !== "data-analytics" && item.id !== "workflow")
                    .map((modulo) => {
                      return { label: t(modulo.label), value: modulo.id };
                    })}
                  value={modulosSelecionados}
                  onChange={setModulosSelecionados}
                  maxTagCount="responsive"
                />
              </Tooltip>
            </Col>
            <Col flex="1 1 150px">
              <Tooltip title={t("Filtrar por acesso")}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  placeholder={t("Acesso")}
                  style={{ width: "100%" }}
                  options={acessos.map((acesso) => {
                    return { label: t(acesso.nome), value: String(acesso.id) };
                  })}
                  maxTagCount="responsive"
                  value={acessoSelecionado}
                  onChange={(value) => {
                    setAcessoSelecionado(value);
                    setEmpresasSelecionadas([]);
                  }}
                  maxTagTextLength={10}
                />
              </Tooltip>
            </Col>
            <Col flex="1 1 250px">
              <Tooltip title={t("Filtrar por empresas")}>
                <Select
                  showSearch
                  showArrow
                  optionFilterProp="label"
                  disabled={!acessoSelecionado}
                  mode="multiple"
                  allowClear
                  placeholder={t("Empresas")}
                  style={{ width: "100%" }}
                  options={empresasByAcesso.map((empresa) => {
                    return { label: t(empresa.razaoSocial), value: String(empresa.id) };
                  })}
                  maxTagCount="responsive"
                  value={empresasSelecionadas}
                  onChange={setEmpresasSelecionadas}
                  maxTagTextLength={10}
                />
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title={t("Limpar Filtros")}>
                <Button onClick={handleLimparFiltros} style={{ borderRadius: 6 }} icon={<ClearOutlined />} />
              </Tooltip>
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );
}
